import React, { useMemo, useState, createContext, useContext } from 'react';
import PropTypes from 'prop-types';

import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeProvider as MUIThemeProvider } from '@mui/material/styles';

import { palette } from './palette';
import { shadows } from './shadows';
import { overrides } from './overrides';
import { typography } from './typography';
import { customShadows } from './custom-shadows';

// 1. Create a context to hold the theme updater function
const ThemeUpdateContext = createContext({
  primaryColor: '#1877F2',
  setPrimaryColor: (mspro: string) => {},
});

export const themeStatic = {
  palette: palette(),
}

// Custom hook to easily access the updater function
export const useThemeUpdater = () => useContext(ThemeUpdateContext);

// 2. Refactor ThemeProvider to manage state
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [primaryColor, setPrimaryColor] = useState('#1877F2'); // Default color

  const setPrimaryColorCallback = React.useCallback((color: string) => {
    setPrimaryColor(color);
  }, []);

  // Memoize the theme creation so it only runs when the color changes
  const theme = useMemo(() => {
    const isMsPro = primaryColor === '#185a84ff';
    const themeOptions = {
      palette: palette(isMsPro), // Pass the dynamic color
      typography,
      shadows: shadows(),
      customShadows: customShadows(),
      shape: { borderRadius: 8 },
    };
    
    const newTheme = createTheme(themeOptions as any);
    newTheme.components = overrides(newTheme) as any;
    
    return newTheme;
  }, [primaryColor]);

  // Memoize the context value to prevent unnecessary re-renders
  const themeUpdateValue = useMemo(() => ({
    primaryColor,
    setPrimaryColor: setPrimaryColorCallback,
  }), [primaryColor]);

  return (
    <ThemeUpdateContext.Provider value={themeUpdateValue}>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ThemeUpdateContext.Provider>
  );
}

ThemeProvider.propTypes = {
  children: PropTypes.node,
};

// You can remove the static theme export if it's no longer needed elsewhere
// export default theme;